/**
 * sl-k94y.7.1: I18nFieldEditor — reusable per-locale text editor.
 *
 * SOLID:
 * - S: Locale-tabbed text editing only — no HTTP, no domain logic, no
 *      submit. Translate-action is delegated via `onTranslate` prop.
 * - D: Decoupled from translate transport. Caller passes a callback that
 *      decides which endpoint (workspace-scoped vs admin-scoped) and how
 *      to authenticate. Editor knows nothing about URLs.
 *
 * Why no `translateUrl` prop: the same editor is reused by the publish
 * wizard (workspace-scoped POST) and the admin TemplateContentEditor
 * (admin-scoped POST). Hard-coding a URL would tie the editor to one
 * caller. Inverting it as a callback keeps both call sites trivial.
 *
 * Required-locale UX: tabs whose value is empty AND the locale appears
 * in `requiredLocales` get a `tabMissingRequired` class so the
 * publish-wizard can surface "you still need a defaultLocale entry" via
 * the same chrome instead of a separate validation panel.
 *
 * Async contract (sl-k94y.7.1 R1):
 * - `onTranslate` MAY reject. The editor catches and routes the error to
 *   the optional `onTranslateError` callback; without that callback the
 *   error is logged via `console.error` so it never becomes a silent
 *   unhandled rejection.
 * - User edits made WHILE a translation is in-flight win over the
 *   translate result. The editor merges the result into the latest
 *   `value` (tracked via ref), so the user is never silently overwritten.
 *
 * Tab-pattern decision (sl-k94y.7.1 R1):
 * - Originally rendered with `role="tablist"` / `role="tab"`. ARIA-
 *   complete tab patterns require `tabpanel`, `aria-controls`,
 *   `aria-labelledby`, and arrow-key navigation. Without all four it's a
 *   half-implemented contract that lies to assistive tech. Downgraded to
 *   plain buttons + `aria-pressed` (toggle-button pattern). Cleaner,
 *   honest a11y, and full keyboard-tab navigation works out-of-box.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import * as styles from './i18n-field-editor.css';

export interface I18nFieldEditorProps {
  /** Current value map keyed by BCP-47 locale tag. */
  value: Record<string, string>;
  /** Receives the next full map. */
  onChange: (next: Record<string, string>) => void;
  /** Locales rendered as buttons, in display order. */
  locales: ReadonlyArray<string>;
  /** Locale opened initially. Must be in `locales`. */
  defaultLocale: string;
  /** Field label rendered above the locale-button row. */
  label: string;
  placeholder?: string;
  /**
   * Locales whose entry MUST be non-empty for the publish boundary to
   * accept. Empty entries get a visual marker on the button. Validation
   * is the caller's responsibility — the editor only signals.
   */
  requiredLocales?: ReadonlyArray<string>;
  /** `true` → multiline `<textarea>`, `false` → single-line `<input>`. */
  multiline?: boolean;
  /**
   * If provided, a "Translate from <source>" control is rendered. The
   * callback receives the source locale + its current value and returns
   * a `{ [targetLocale]: text }` map for every locale the caller wants
   * to fill. The editor merges the result into the latest `value` so
   * concurrent user edits are preserved.
   */
  onTranslate?: (
    sourceLocale: string,
    sourceText: string
  ) => Promise<Record<string, string>>;
  /**
   * Optional notifier for translate failures. Called when `onTranslate`
   * rejects so the caller can surface a toast / error UI. Without this
   * the editor logs to `console.error` to avoid silent unhandled
   * rejections.
   */
  onTranslateError?: (error: unknown) => void;
  disabled?: boolean;
}

function clampToLocales(
  candidate: string,
  locales: ReadonlyArray<string>
): string {
  if (locales.includes(candidate)) return candidate;
  return locales[0] ?? candidate;
}

export function I18nFieldEditor({
  value,
  onChange,
  locales,
  defaultLocale,
  label,
  placeholder,
  requiredLocales,
  multiline,
  onTranslate,
  onTranslateError,
  disabled,
}: I18nFieldEditorProps) {
  const [activeLocale, setActiveLocale] = useState(() =>
    clampToLocales(defaultLocale, locales)
  );
  const [translateSource, setTranslateSource] = useState(() =>
    clampToLocales(defaultLocale, locales)
  );
  const [translating, setTranslating] = useState(false);
  const fieldId = useId();

  // sl-k94y.7.1 R1: track latest value so an in-flight translate doesn't
  // overwrite user edits committed AFTER the translate started.
  const latestValueRef = useRef(value);
  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  // sl-k94y.7.1 R1: re-clamp when locales change so the component never
  // edits a stale `value['']` slot if the deployment's available-locales
  // set shifts mid-session. We DON'T reset to `defaultLocale` here — that
  // would clobber a user's explicit tab choice every time the parent
  // re-renders with a fresh array literal. Clamping `prev` is a no-op
  // when prev is still in locales, so reference instability is harmless.
  // We key on the joined locale list to avoid array-identity churn from
  // unstable parent props.
  // (Use JSON.stringify to keep the file ASCII-clean — a literal separator
  // can land in the source as a control byte depending on how the file
  // was edited, and tooling treats such files as binary.)
  const localesKey = JSON.stringify(locales);
  useEffect(() => {
    setActiveLocale(prev => clampToLocales(prev, locales));
    setTranslateSource(prev => clampToLocales(prev, locales));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localesKey]);

  const required = requiredLocales ?? [];
  const isMissingRequired = (locale: string): boolean =>
    required.includes(locale) && (value[locale] ?? '').trim().length === 0;

  const handleInputChange = useCallback(
    (text: string) => {
      onChange({ ...latestValueRef.current, [activeLocale]: text });
    },
    [activeLocale, onChange]
  );

  const handleTranslateClick = useCallback(async () => {
    if (!onTranslate || translating || disabled) return;
    const sourceText = (latestValueRef.current[translateSource] ?? '').trim();
    if (sourceText.length === 0) return;
    // sl-k94y.7.1 R2: snapshot pre-await so we only overwrite TARGET locales
    // the user did NOT touch while the translate was in flight. The previous
    // shallow merge `{...latest, ...result}` clobbered any target-locale
    // edit (e.g. user types in DE while EN is translating).
    const snapshot = latestValueRef.current;
    setTranslating(true);
    try {
      const result = await onTranslate(translateSource, sourceText);
      const merged: Record<string, string> = { ...latestValueRef.current };
      for (const [locale, text] of Object.entries(result)) {
        const userTouched =
          (latestValueRef.current[locale] ?? '') !== (snapshot[locale] ?? '');
        if (!userTouched) merged[locale] = text;
      }
      onChange(merged);
    } catch (err) {
      if (onTranslateError) {
        onTranslateError(err);
      } else {
        console.error('[I18nFieldEditor] onTranslate rejected:', err);
      }
    } finally {
      setTranslating(false);
    }
  }, [
    disabled,
    onChange,
    onTranslate,
    onTranslateError,
    translateSource,
    translating,
  ]);

  return (
    <div className={styles.fieldRoot}>
      <div className={styles.labelRow}>
        <label className={styles.fieldLabel} htmlFor={fieldId}>
          {label}
          {required.length > 0 && (
            <span className={styles.requiredMark} aria-hidden>
              *
            </span>
          )}
        </label>
        {onTranslate && (
          <div className={styles.translateRow}>
            <select
              className={styles.translateSelect}
              value={translateSource}
              disabled={disabled || translating}
              onChange={e => setTranslateSource(e.target.value)}
              aria-label="Source locale"
            >
              {locales.map(locale => (
                <option key={locale} value={locale}>
                  {locale.toUpperCase()}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.translateButton}
              disabled={
                disabled ||
                translating ||
                (value[translateSource] ?? '').trim().length === 0
              }
              onClick={() => void handleTranslateClick()}
            >
              {translating
                ? 'Translating…'
                : `Translate from ${translateSource.toUpperCase()}`}
            </button>
          </div>
        )}
      </div>

      <div className={styles.tabRow}>
        {locales.map(locale => {
          const active = locale === activeLocale;
          const missing = isMissingRequired(locale);
          return (
            <button
              key={locale}
              type="button"
              aria-pressed={active}
              aria-invalid={missing && active ? true : undefined}
              className={[
                styles.tab,
                active ? styles.tabActive : '',
                missing && !active ? styles.tabMissingRequired : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setActiveLocale(locale)}
            >
              {locale.toUpperCase()}
              {missing && !active && (
                <span aria-hidden style={{ marginLeft: 2 }}>
                  *
                </span>
              )}
            </button>
          );
        })}
      </div>

      {multiline ? (
        <textarea
          id={fieldId}
          className={styles.textarea}
          value={value[activeLocale] ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          onChange={e => handleInputChange(e.target.value)}
        />
      ) : (
        <input
          id={fieldId}
          className={styles.input}
          value={value[activeLocale] ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          onChange={e => handleInputChange(e.target.value)}
        />
      )}
    </div>
  );
}
